import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import ModalAlert from '../../../components/ModalAlert/ModalAlert';
import Modal from '../../../components/Modal/Modal';
import Badge from '../../../components/Badge/Badge';
import editIcon from '../../../assets/editIcon.png';
import toggleActive from '../../../assets/toggleActive.png';
import toggleInactive from '../../../assets/toggleInactive.png';
import './UserManagement.css';
//services
import { usersList } from '../../../services/user';
import { role } from '../../../services/role';
import { modulePermissions } from '../../../utils/modulePermissions';
import { customer } from '../../../services/customer';
import Tooltip from '../../../components/Tooltip/Tooltip';

const UserManagement = () => {
  const { searchTerm } = useSelector((state) => state.search);
  const navigate = useNavigate();
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState({ text: '', highlight: '' });
  const [message, setMessage] = useState('');
  const [isModalMessage, setIsModalMessage] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [columns, setColumns] = useState([]);
  const [columnsShare, setColumnsShare] = useState(0);
  const [error, setError] = useState(false);
  const [titleAction, setTitleAction] = useState('');
  const [roles, setRoles] = useState([{
    id: 0,
    name: ''
  }]);
  const [users, setusers] = useState([{
    id: 0,
    name: '',
    phone: '',
    email: '',
    role: '',
    isActive: false
  }]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchColumns();
  }, []);

  useEffect(() => {
    // Debounce la búsqueda para no hacer demasiadas peticiones
    const timeoutId = setTimeout(() => {
      fetchUsers(selectedRole, selectedStatus, searchTerm, currentPage);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedRole, selectedStatus]);

  const fetchUsers = async (role = '', user_status = '', query = '', page = 1) => {
    try {
      const params = {
        page: page,
        items_per_page: 9,
        ...(role && { role }),
        ...(user_status && { user_status }),
        ...(query && { query })
      };
      const response = await usersList.getDataUser(params);
      setusers([]);
      if(response.status === "success") {
        setTotalItems(response.data.total);
        const users = response.data.data.map(user => {
          let role = user.roles[0]
          return {
            id: user.id,
            name: user.name,
            phone: user.phone,
            email: user.email,
            role: role,
            role_id: user.roles[0],
            status: user.status === 'active' ? 'Active' : 'Inactive',
            user: user.user
          }
        });
        setusers(users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await role.getDataRole();
      setRoles([])
      if(response.status === "success") {
        response.data.filter(role => role.name !== 'user').forEach(role => {
          setRoles(prev => [...prev, role]);
        });
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchColumns = async () => {
    let count = 0;
    const columnsCurrentUser = [
      { header: 'Name', field: 'name' },
      { header: 'Phone', field: 'phone' },
      { header: 'Email', field: 'email' },
      { header: 'Role', field: 'role' },
      { header: 'Status', field: 'status' }
    ];
    if(modulePermissions.hasUserManagementPermission('edit')) {
      columnsCurrentUser.push({
        header: '',
        render: (row) => (
          <Tooltip text="Edit user">
            <button className="edit-btn" onClick={() => handleEdit(row)}>
              <img src={editIcon} alt="Edit" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }
    if(modulePermissions.hasUserManagementPermission('activate_deactivate')) {
      columnsCurrentUser.push({
        header: '',
        render: (row) => (
          <Tooltip text="Enable or Disable this user">
            <button className="edit-btn" onClick={() => handleToggle(row)}>
              <img src={row.status === 'Active' ? toggleActive : toggleInactive} alt="Toggle" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }
    setColumnsShare(count);
    setColumns(columnsCurrentUser);
  };

  const handleEdit = (row) => {
    if(row.status !== 'Active') {
      setTitle({ text: 'Not', highlight: 'available' });
      setMessage('User disabled, action unavailable');
      setError(true);
      setIsModalMessage(true);
      return;
    }
    navigate(`/users/edit-user/${row.id}`, { 
      state: { 
        isEdit: true, 
        totUsers: totalItems,
        user: {
          id: row.id,
          name: row.name,
          phone: row.phone,
          email: row.email,
          user: row.user,
          role: row.role_id,
        }
      } 
    });
  };

  const handleToggle = (row) => {
    setSelectedUser(row);
    const title = row.status === 'Active' ? 'Disable' : 'Enable';
    setTitleAction(title);
    setIsModalOpen(true);
  };

  const handleModalMessageClose = () => {
    setIsModalMessage(false);
    setSelectedUser(null);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeactivate = async () => {
    console.log('Deactivating user:', selectedUser);
    setIsModalOpen(false);
    try {
      const status = selectedUser.status === 'Active' ? 'disabled' : 'active';
      const title = selectedUser.status === 'Active' ? 'disable' : 'enable';
      const message = selectedUser.status === 'Active' ? 'The client is now disabled' : 'The client is now enabled';
      const response = await customer.toggleStatus(selectedUser.id, status);
      if (response.status === 'success') {
        setTitle({ text: 'Client successfully ', highlight: title });
        setMessage(message);
        setError(false);
        setIsModalMessage(true);
        fetchUsers();
      }
    } catch (error) {
      const title = selectedUser.status === 'Active' ? 'disabling' : 'enabling';
      setTitle({ text: 'Error to ', highlight: title });
      setMessage('Failed to update customer status');
      setError(true);
      setIsModalMessage(true);
    }
  };

  const handleRoleFilter = (option) => {
    setSelectedRole(option.label);
    fetchUsers(option.label);
  };

  const handleStatusFilter = (option) => {
    setSelectedStatus(option.value);
    fetchUsers('', option.value);
  };

  const handleDownload = () => {
    const params = {
      ...(selectedRole && { role: selectedRole }),
      ...(selectedStatus && { user_status: selectedStatus })
    };
    usersList.downloadUsers(params);
  };

  return (
    <div className="users-page">
      <div className="users-container">
        <div className="actions-bar">
          <div className="content-filter">
            <Dropdown
              buttonText="Filters"
              options={[
                { 
                  label: 'Role',
                  value: 'role',
                  onClick: () => {
                    setIsRoleFilterOpen(!isRoleFilterOpen);
                    if(isRoleFilterOpen === true) {
                      fetchUsers();
                    }
                  }
                },
                { 
                  label: 'Status',
                  value: 'status',
                  onClick: () => {
                    setIsStatusFilterOpen(!isStatusFilterOpen);
                    if(isStatusFilterOpen === true) {
                      fetchUsers();
                    }
                  }
                }
              ]}
              onSelect={(option) => option.onClick()}
            />
            {isRoleFilterOpen && (
              <Dropdown
                buttonText="Role"
                options={roles.map(role => ({
                  label: role.name,
                  value: role.id
                }))}
                onSelect={(option) => handleRoleFilter(option)}
                showSelected={true}
                closeSelect={true}
                closeDropdown={() => {
                  console.log('closeDropdown');
                  setIsRoleFilterOpen(!isRoleFilterOpen);
                  fetchUsers();
                }}
              />
            )}
            {isStatusFilterOpen && (
              <Dropdown
                buttonText="Status"
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'disabled' }
                ]}
                onSelect={(option) => handleStatusFilter(option)}
                showSelected={true}
                closeSelect={true}
                closeDropdown={() => {
                  setIsStatusFilterOpen(!isStatusFilterOpen);
                  fetchUsers();
                }}
              />
            )}
          </div>
          <div className="action-buttons">
            {modulePermissions.hasUserManagementPermission('create') && (
              <Button
                onClick={() => navigate('/users/new-user', { 
                  state: { 
                    isEdit: false, 
                    totUsers: totalItems 
                }
              })}
              variant="primary"
              borderRadius={6}
            >
              New user
            </Button>
            )}
            {modulePermissions.hasUserManagementPermission('download') && (
              <Button
                onClick={() => handleDownload()}
                variant="warning"
                borderRadius={6}
              >
                Download
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          data={users}
          columnsShare={columnsShare}
          itemsPerPage={9}
          currentPage={currentPage}
          totalItems={totalItems}
          onPageChange={(page) => {
            setCurrentPage(page);
            fetchUsers(selectedRole, selectedStatus, searchTerm, page);
          }}
        />

        <Modal
          isOpen={isModalMessage}
          title={title}
          message={message}
          error={error}
          onConfirm={handleModalMessageClose}
        />

        <ModalAlert
          isOpen={isModalOpen}
          onClose={handleClose}
          title={{ text: titleAction, highlight: 'user' }}
          message='Do you want to continue with the action?'
          acceptText={selectedUser?.isActive ? 'Deactivate' : 'Activate'}
          acecept={true}
          onAccept={handleDeactivate}
          cancelText="Cancel"
          onCancel={handleClose}
        />
      </div>
    </div>
  );
};

export default UserManagement;